import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2Icon, PlusIcon } from 'lucide-react';
import { FeedbackCard, type Feedback } from '@/components/feedback/FeedbackCard';
import { FeedbackFormSheet } from '@/components/feedback/FeedbackFormSheet';
import { useFeedback, useCreateFeedback, useDeleteFeedback } from '@/hooks/use-feedback';
import type { CreateFeedbackRequest } from '@/lib/api/feedback';

export const Route = createFileRoute('/admin/employees/$id/feedback')({
  component: EmployeeFeedbackPage,
});

function EmployeeFeedbackPage() {
  const { id } = Route.useParams();
  const { data: feedback, isLoading, error } = useFeedback(id);
  const createFeedbackMutation = useCreateFeedback(id);
  const deleteFeedbackMutation = useDeleteFeedback(id);

  const [showFeedbackSheet, setShowFeedbackSheet] = useState(false);

  const handleCreateFeedback = async (data: CreateFeedbackRequest) => {
    await createFeedbackMutation.mutateAsync(data);
    setShowFeedbackSheet(false);
  };

  const handleDeleteFeedback = async (feedbackItem: Feedback) => {
    if (confirm('Are you sure you want to delete this feedback?')) {
      await deleteFeedbackMutation.mutateAsync(feedbackItem.id);
    }
  };

  const handleAddFeedbackClick = () => {
    setShowFeedbackSheet(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-destructive">Failed to load feedback. Please try again.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {feedback && feedback.length > 0 ? (
          <div className="grid gap-4">
            {feedback.map((item) => (
              <FeedbackCard
                key={item.id}
                feedback={item}
                onDelete={handleDeleteFeedback}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No feedback received yet</p>
              <Button onClick={handleAddFeedbackClick} variant="outline">
                <PlusIcon className="h-4 w-4 mr-2" />
                Add First Feedback
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Feedback Form Sheet */}
      <FeedbackFormSheet
        open={showFeedbackSheet}
        onOpenChange={setShowFeedbackSheet}
        employeeId={id}
        onSubmit={handleCreateFeedback}
        isSubmitting={createFeedbackMutation.isPending}
      />
    </>
  );
}
